'use client';

import type {StackProps, SystemStyleObject} from '@chakra-ui/react';
import {Heading, Text} from '@chakra-ui/react';
import type {ReactNode} from 'react';
import {createContext, useContext, useEffect} from 'react';

import {Box, HStack, VStack} from '../index';
import {usePageTitleStore} from './page-title-store';

/**
 * Page layout components.
 * @example
 * <PageLayout>
 *    <PageLayoutHeader
 *      title={'Page title'}
 *      subtitle={'Last login: Apr 29, 2024 at 20:33'}
 *      tags={[<Badge>Badge name</Badge>, <Badge>Inactive User</Badge>]}
 *      avatar={<Avatar />}
 *      extra={<Button variant={'primary'}>Export</Button>}
 *    />
 *    <PageLayoutContent>Content</PageLayoutContent>
 *  </PageLayout>
 */
export type PageLayoutProps = {
  children: ReactNode;
  emptyState?: ReactNode;
  isEmpty?: boolean;
  sx?: SystemStyleObject;
  isInnerPage?: boolean;
};

const PageLayoutContext = createContext({isInnerPage: false});

export const usePageLayout = () => useContext(PageLayoutContext);

const PageLayoutInternal = (props: PageLayoutProps) => {
  const {children, emptyState, isEmpty, sx = {}, isInnerPage = false} = props;

  if (isEmpty && emptyState) {
    return (
      <VStack height={`full`} justifyContent={`center`}>
        {emptyState}
      </VStack>
    );
  }

  return (
    <PageLayoutContext.Provider value={{isInnerPage}}>
      <VStack
        sx={{
          height: `full`,
          py: `space-250`,
          px: `space-250`,
          gap: isInnerPage ? `space-150` : `space-150`,
          alignItems: `stretch`,
          ...sx,
        }}
      >
        {children}
      </VStack>
    </PageLayoutContext.Provider>
  );
};

type TitleAsString = {
  title: string | number | undefined;
  /** Plain-text version of the title, used for the document/page title. */
  titleText?: string;
};
/** Used when title is a ReactNode — requires titleText for plain-text consumers (e.g. page title, AI context). */
type TitleAsNode = {
  title: Exclude<ReactNode, string | number | undefined>;
  /**
   * Required when title is a ReactNode — provides the plain-text version used for the document/page title.
   * Used for components that read the title text as a string (for example PageLayoutHeader component which passes title to use-page-title.ts)
   */
  titleText: string;
};

export type PageLayoutHeaderProps = Omit<StackProps, 'title'> & {
  subtitle?: ReactNode;
  avatar?: ReactNode;
  tags?: ReactNode;
  extra?: ReactNode;
} & (TitleAsString | TitleAsNode);

export const PageLayoutHeader = (props: PageLayoutHeaderProps) => {
  const {title, titleText, subtitle, extra, tags, avatar, ...stackProps} = props;
  const {isInnerPage} = usePageLayout();

  useEffect(() => {
    const text = titleText ?? (typeof title === 'string' ? title : '');
    usePageTitleStore.getState().setPageTitle(text);
    return () => usePageTitleStore.getState().setPageTitle('');
  }, [title, titleText]);

  return (
    <HStack gap={`space-200`} paddingTop={isInnerPage ? `space-100` : undefined} {...stackProps}>
      {avatar}
      <Box width={`full`}>
        <VStack alignItems={`flex-start`} gap={`space-050`}>
          <HStack width={`full`} gap={`space-200`}>
            <Heading data-testid={`page-title`} size={`md`} as={isInnerPage ? `h2` : `h1`}>
              {title}
            </Heading>
            <HStack gap={`space-100`}>{tags}</HStack>
            {extra && <Box marginLeft={`auto`}>{extra}</Box>}
          </HStack>
          {subtitle && (
            <Text color={`text.muted`} fontSize={`sm`} width={`full`}>
              {subtitle}
            </Text>
          )}
        </VStack>
      </Box>
    </HStack>
  );
};

export type PageLayoutContentProps = {children: ReactNode; sx?: SystemStyleObject};

export const PageLayoutContent = ({children, sx = {}}: PageLayoutContentProps) => (
  <VStack
    data-name={`page-content`}
    sx={{
      overflow: `hidden`,
      flexGrow: 1,
      alignItems: `stretch`,
      [`& > *`]: {
        flex: `0 1 auto`,
      },
      [`& > :last-child`]: {
        flex: 1,
      },
      ...sx,
    }}
  >
    {children}
  </VStack>
);

// Add compound components
PageLayoutInternal.Header = PageLayoutHeader;
PageLayoutInternal.Content = PageLayoutContent;

// Export the main component
export const PageLayout = PageLayoutInternal;
